import Color from 'color';

import { ColorScheme, AccessbileTextScheme } from './ColorScheme';
import { getPaletteAsRawColors, getPalette, ThemeConstants } from 'owa-theme';
import strings from 'owa-strings/owa-calendar-strings';
import { ITheme, createTheme, IPalette } from '@uifabric/styling';
import { getIsDarkTheme } from 'owa-fabric-theme';

const MINIMUM_TEXT_CONTRAST = 4.5;
let shadeValues = {
    calDarker: -44,
    calDark: -24,
    calDarkAlt: -10,
    calPrimary: 0,
    calSecondary: 12,
    calTertiary: 40,
    calLight: 70,
    calLighter: 84,
    calLighterAlt: 96,
};

export const DEFAULT_COLOR: string = getPaletteAsRawColors(
    ThemeConstants.BASE_THEME_ID
).themePrimary.toUpperCase();

/**
 * Use for shim purposes only
 *
 * Deprecated in favor of #DB3A00 in order to satisfy strict check for minimum A11Y contrast ratio
 * when white text/icons are placed on this background color
 */
export const DEPRECATED_ORANGE_CALENDAR_COLOR = '#DC3B00';

export const ORANGE_CALENDAR_COLOR = '#DB3A00';

/**
 * Return whether the color scheme's primary color is the default color or not
 */
export function isDefaultColor(colorScheme: ColorScheme): boolean {
    return colorScheme.calPrimary.toUpperCase() === DEFAULT_COLOR.toUpperCase();
}

/**
 * These are the default 9 calPrimary colors for users to pick from without picking their own custom hex values
 */
const defaultPrimaryColors = {
    [DEFAULT_COLOR]: () => strings.blueCalendarColorText, // blue
    '#107C10': () => strings.greenCalendarColorText, // green
    '#CF0F1F': () => strings.redCalendarColorText, // Red
    '#0127C5': () => strings.indigoCalendarColorText, // Indigo
    '#666666': () => strings.grayCalendarColorText, // Gray
    '#0B857D': () => strings.tealCalendarColorText, // Teal
    '#E3008C': () => strings.pinkCalendarColorText, // Pink
    [ORANGE_CALENDAR_COLOR]: () => strings.orangeCalendarColorText, // Orange
    '#881798': () => strings.violetCalendarColorText, // Violet
};

/**
 * Adds the given opacity to the given color value and returns a string representation of the result
 */
export function addOpacityToColor(colorString: string, opacity: number): string {
    let color = Color(colorString);
    return color.alpha(opacity).toString();
}

/**
 * We dynamically generate a color scheme given a primary color value by taking the primary color and shading it with a % of black and % of white
 *
 * The percentages of white and black come from shadeValues, and create a ColorScheme where the given primary color corresponds to calPrimary, with each darker
 * shade mixing extra black and each lighter shade mixing extra white.
 *
 * Code from universal color generation is here:
 * https://msasg.visualstudio.com/_search?type=code&lp=custom-Collection&text=CalendarColorShadeGenerator&result=DefaultCollection%2FOfficeSourceDepot%2Fdevmain%2Fdevmain%2Fhxcalendarui%2Fxaml%2FSupport%2FcolorShadeGenerator%2FCalendarColorShadeGenerator.cpp&preview=1&_a=search
 *
 * @param primaryHexColor the string value of the color to use as primary
 */
export function generateColorScheme(primaryHexColor: string): ColorScheme {
    let primaryColor = Color(primaryHexColor);
    let colorScheme: ColorScheme = {
        text: '',
        textAlt: '',
        accessibleText: {
            calPrimaryBackground: '',
            calLighterBackground: '',
            msColorWhiteBackground: '',
            msColorNeutralLighterBackground: '',
        },
        calDarker: shadeColor(
            primaryColor,
            getIsDarkTheme() ? shadeValues.calLighterAlt : shadeValues.calDarker
        ).hex(),
        calDark: shadeColor(
            primaryColor,
            getIsDarkTheme() ? shadeValues.calLighter : shadeValues.calDark
        ).hex(),
        calDarkAlt: shadeColor(
            primaryColor,
            getIsDarkTheme() ? shadeValues.calLight : shadeValues.calDarkAlt
        ).hex(),
        calPrimary: primaryColor.hex(),
        calSecondary: shadeColor(
            primaryColor,
            getIsDarkTheme() ? -shadeValues.calSecondary : shadeValues.calSecondary
        ).hex(),
        calTertiary: shadeColor(
            primaryColor,
            getIsDarkTheme() ? -shadeValues.calTertiary : shadeValues.calTertiary
        ).hex(),
        calLight: shadeColor(
            primaryColor,
            getIsDarkTheme() ? shadeValues.calDarkAlt : shadeValues.calLight
        ).hex(),
        calLighter: shadeColor(
            primaryColor,
            getIsDarkTheme() ? shadeValues.calDark : shadeValues.calLighter
        ).hex(),
        calLighterAlt: shadeColor(
            primaryColor,
            getIsDarkTheme() ? shadeValues.calDarker : shadeValues.calLighterAlt
        ).hex(),
        colorName: () => getColorName(primaryColor.hex()),
    };

    colorScheme.accessibleText = calculateAccessibleText(colorScheme);
    colorScheme.text = colorScheme.accessibleText.calPrimaryBackground;
    colorScheme.textAlt = colorScheme.accessibleText.calLighterBackground;

    return colorScheme;
}

/**
 * Computes the result of overlapping multiple colors on top of each other. This only has meaning if the colors
 * overlapping have opacity to them- if the top color has alpha = 1.0, the result will just be the top color.
 *
 * this is called alpha compositing: https://en.wikipedia.org/wiki/Alpha_compositing, an implementation of the "over" operation
 * @param colors the colors to overlap. Descending order (first color is on top, second color is the second from the top, etc.)
 * @returns string representation of the resulting color.
 */
export function computeResultingOverlapColor(...colors: string[]): string {
    let total = Color(colors[colors.length - 1]);

    for (let i = colors.length - 2; i >= 0; i--) {
        let top = Color(colors[i]);
        let totalAlpha = top.alpha() + total.alpha() * (1 - top.alpha());
        total = Color({
            r: Math.floor(
                (top.alpha() * top.red() + total.alpha() * (1 - top.alpha()) * total.red()) /
                    totalAlpha
            ),
            g: Math.floor(
                (top.alpha() * top.green() + total.alpha() * (1 - top.alpha()) * total.green()) /
                    totalAlpha
            ),
            b: Math.floor(
                (top.alpha() * top.blue() + total.alpha() * (1 - top.alpha()) * total.blue()) /
                    totalAlpha
            ),
            alpha: totalAlpha,
        });
    }

    return total.toString();
}

/**
 * Calculates optimal text color from the colorScheme for the given background color
 *
 * given an the background color and an array of possible colors to use for text on it, we find the closest of the given colors + white/black
 * to the minimum contrast that still exceeds the minimum contrast. If the best color is black, returns a color with the same Hue and Saturation as
 * calDarker, and a brightness Value set to as bright as possible while still passing minimum contrast
 *
 * @param backgroundColor the background color for the text to go on
 * @param colorArray the array of colors to search for a possible value. Will also try black and white
 * @param desiredColorString the color to use assuming it passes the contrast check
 * @param minimumContrast the minimum contrast the text must pass
 */
export function getOptimalTextColorString(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColorString: string,
    minimumContrast: number = MINIMUM_TEXT_CONTRAST
): string {
    // first see if desired color passes, if so we can just return that immediately
    let background = Color(backgroundColor);
    let desiredColor = Color(desiredColorString);
    if (getContrastRatio(desiredColor, background) >= minimumContrast) {
        return desiredColor.hex();
    }

    // if desired color doesn't pass, look through all the colors in the colorScheme, and find the one closest to
    // the minimum contrast that still passes, if any
    let closestContrast = 21; // contrast between black and white, largest value for a contrast
    let bestColor: Color;
    // also keep track of the contrast and color that come closest to satisfying the minimum contrast, in case we are never able to find a value that passes
    let fallbackContrast = 0;
    let fallbackColor: Color;
    Object.values(colorScheme).forEach(colorString => {
        if (colorString && typeof colorString == 'string') {
            let color = Color(colorString);
            let contrast = getContrastRatio(color, background);
            if (contrast >= minimumContrast && contrast < closestContrast) {
                bestColor = color;
                closestContrast = contrast;
                // keep track of most optimal fallback color/contrast
            } else if (contrast < minimumContrast && contrast > fallbackContrast) {
                fallbackColor = color;
                fallbackContrast = contrast;
            }
        }
    });

    if (bestColor) {
        return bestColor.hex();
    }

    // if we still haven't found a color good enough, we need to check black and white (adding both black and white guarantees we'll pass)
    // check white first over black
    let whiteColor = Color({ r: 255, g: 255, b: 255 });
    let whiteContrast = whiteColor.contrast(background);
    if (whiteContrast >= minimumContrast) {
        return whiteColor.hex();
        // continue tracking most optimal fallback color/contrast
    } else if (whiteContrast < minimumContrast && whiteContrast > fallbackContrast) {
        fallbackColor = whiteColor;
        fallbackContrast = whiteContrast;
    }

    // finally, we're forced to use black. But we'd ideally like at least a shade of the color that's the same as primary, so rather than just use black
    // we'll search for a value that's dark enough to pass contrast, but still the same hue and saturation as the primary value
    // so we'll start from calPrimary and binary search between calDarker and black, adjusting the V value of the HSV,
    // to find the lightest color of the same H and S value that still passes minimumContrast
    let currentColor = Color(colorScheme.calPrimary);

    let lowerVBound = 0;
    let upperVBound = currentColor.value();

    // we take 7 steps because binary searching for a V value between 0 and 100, 2^7 = 128, so we're sure we'll hit a good value
    for (let steps = 0; steps < 7; steps++) {
        let currentV = (upperVBound + lowerVBound) / 2;

        currentColor = currentColor.value(currentV);
        let newContrast = currentColor.contrast(background);

        // continue tracking most optimal fallback color/contrast
        if (newContrast < minimumContrast && newContrast > fallbackContrast) {
            fallbackColor = currentColor;
            fallbackContrast = newContrast;
        }

        if (newContrast < minimumContrast + 0.1) {
            upperVBound = currentV;
        } else {
            lowerVBound = currentV;
        }
    }

    // choose the color that passes the minimum contrast, or else comes closest to satisfying the minimum contrast
    let currentContrast = currentColor.contrast(background);
    if (currentContrast < minimumContrast && fallbackColor && currentContrast < fallbackContrast) {
        return fallbackColor.hex();
    }
    return currentColor.hex();
}

/**
 * Generates the default color schemes and returns them as an array
 */
export function generateDefaultColorSchemes(): ColorScheme[] {
    return Object.keys(defaultPrimaryColors).map(color => generateColorScheme(color));
}

/**
 * Create a Fabric Theme from calendar color scheme
 * @param scheme calendar ColorScheme object
 * @return the theme based on this color scheme
 */
export function themeFromColorScheme(scheme: ColorScheme): ITheme {
    return createTheme({
        palette: {
            ...(getPalette() as IPalette),
            themePrimary: scheme.calPrimary,
            themeLighterAlt: scheme.calLighterAlt,
            themeLighter: scheme.calLighter,
            themeLight: scheme.calLight,
            themeTertiary: scheme.calTertiary,
            themeSecondary: scheme.calSecondary,
            themeDarkAlt: scheme.calDarkAlt,
            themeDark: scheme.calDark,
            themeDarker: scheme.calDarker,
        },
    });
}

/**
 * Calculates accessible passing text colors for various background colors. Uses the color scheme as a base and adjusts the color as necessary to get
 * minimum contrast for the given backgrounds.
 * @param colorScheme the color scheme
 */
function calculateAccessibleText(colorScheme: ColorScheme): AccessbileTextScheme {
    let whiteColor = getPaletteAsRawColors().white;

    return {
        calPrimaryBackground: getOptimalTextColorString(
            colorScheme.calPrimary,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
        calLighterBackground: getOptimalTextColorString(
            colorScheme.calLighter,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
        msColorWhiteBackground: getOptimalTextColorString(
            getPaletteAsRawColors().white,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
        msColorNeutralLighterBackground: getOptimalTextColorString(
            getPaletteAsRawColors().neutralLighter,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
    };
}

function getColorName(color: string): string {
    return defaultPrimaryColors[color] ? defaultPrimaryColors[color]() : color;
}

function getContrastRatio(color: Color, backgroundColor: Color): number {
    return Math.floor(color.contrast(backgroundColor) * 100) / 100;
}

/**
 * Shades in black or white into the given RGB value and returns a new value.
 * @param color the Color to shade
 * @param amount the amount to shade, [-100, 100]. Negative shades in black, positive shades in white i.e. -100 returns black.
 */
function shadeColor(color: Color, amount: number): Color {
    let newColor = Color(color.rgb());
    if (amount == 0) {
        return newColor;
    } else if (amount < 0) {
        // Shade by mixing with [amount]% black.  Black is at V = 0, for any H and S values.
        newColor = newColor.value(color.value() * (1 + amount / 100.0));
    } else {
        // Tint by mixing with [amount]% white.  White is the point where V = 1 and S = 0 for any H value.
        newColor = newColor.value(color.value() + ((100 - color.value()) * amount) / 100.0);
        newColor = newColor.saturationv(color.saturationv() * (1 - amount / 100));
    }

    newColor = newColor.saturationv(clamp(newColor.saturationv()));
    newColor = newColor.value(clamp(newColor.value()));
    return newColor;
}

function clamp(value: number): number {
    if (value < 0) {
        return 0;
    }
    if (value > 100) {
        return 100;
    }

    return value;
}
